import { FunctionComponent, memo, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate } from 'react-router-dom';
import { Slide, ToastContainer } from 'react-toastify';
import {
  DetailsModalAdmin,
  InAppMessageManager,
  Navbar,
  SidebarAdmin,
  TermsModal,
} from '../../components';
import {
  AudioPlayerContext,
  AudioPlayerData,
  ChannelContext,
  ConfigContext,
  DetailsModalContext,
  DetailsModalData,
  EventDetailsModalContext,
  EventDetailsModalData,
  OrganisationContext,
  RightSideContext,
  UserContext,
} from '../../context';
import { Channel, useGetConfigQuery, useOrganisationQuery, User } from '../../services';
import {
  AudioPlayer,
  EventDetailsModal,
  getOrganisationDomain,
  IconLabel,
  LayoutLoader,
  MAIN_CONTENT_ID,
  ModalSlidePanel,
  ScrollToTop,
  SkipLink,
  useAnalytics,
  useAppSelector,
  useBeforeUnload,
  useConfig,
  useDuration,
  useFestiveMode,
  useIsNarrowViewport,
  useLeaderBoard,
  useLinkClick,
  useNotificationsPermissionPopup,
  useUserDevice,
} from '../../shared';
import { useLayoutData } from '../useLayoutData';

import classNames from 'classnames';
import classes from './AdminLayout.module.scss';

interface AdminLayoutProps {
  rightSideModule?: JSX.Element;
  fullWidthContent?: boolean;
}

export const AdminLayout: FunctionComponent<AdminLayoutProps> = memo(
  ({ rightSideModule, fullWidthContent }) => {
    const { t } = useTranslation();

    const navigate = useNavigate();

    const isNarrowViewport = useIsNarrowViewport();

    const { data: layoutData } = useLayoutData({ excludeArchivedChannels: true });

    const { userProfile, userInfo, channelBySubdomain } = { ...layoutData };

    const { channels } = useAppSelector(({ channels }) => channels);

    const { data: organisation } = useOrganisationQuery();

    const { data: config } = useGetConfigQuery({ organisationDomain: getOrganisationDomain() });

    useConfig(config);

    useUserDevice(config);

    useNotificationsPermissionPopup(config);

    const { festiveModeComponent } = useFestiveMode({ config });

    const { startTrackTime, getDuration } = useDuration();

    const { logSessionStart, logSessionEnd } = useAnalytics();

    const { isLeaderBoard } = useLeaderBoard();

    const [userProfileData, setUserProfile] = useState<User | undefined>();

    const [channel, setChannel] = useState<Channel | undefined>();

    const [audioPlayerData, setAudioPlayerData] = useState<AudioPlayerData>({
      userId: '',
      channelId: 0,
      storyId: 0,
      cardId: 0,
      cardTitle: '',
      src: '',
      isPlaying: false,
    });

    const [detailsModalData, setDetailsModalData] = useState<DetailsModalData>({
      storyId: null,
      cardId: null,
    });

    const [eventDetailsModalData, setEventDetailsModalData] = useState<EventDetailsModalData>({
      eventId: null,
    });

    const [isRightPanelOpen, setIsRightPanelOpen] = useState(false);

    const [isRightSideEmpty, setIsRightSideEmpty] = useState(false);

    // redirect in case of no admin rights
    if (channelBySubdomain && !channelBySubdomain.dashboardUrl) {
      navigate('/');
    }

    useEffect(() => {
      if (!userProfile) {
        return;
      }

      setUserProfile(userProfile);
    }, [userProfile]);

    useEffect(() => {
      if (!channelBySubdomain) {
        return;
      }

      setChannel(channelBySubdomain);
    }, [channelBySubdomain]);

    useEffect(() => {
      logSessionStart({ time_stamp: new Date().toISOString() });
      startTrackTime();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useBeforeUnload(() =>
      logSessionEnd({
        channel_id: channelBySubdomain?.id ?? 0,
        duration: getDuration(),
        time_stamp: new Date().toISOString(),
      })
    );

    useLinkClick({
      channelId: channelBySubdomain?.id ?? 0,
      userId: userProfile?.id,
      setDetailsModalData,
    });

    const rightPanel = useMemo(() => {
      if (!isNarrowViewport) {
        return rightSideModule;
      }

      if (!rightSideModule) {
        return null;
      }

      return (
        <ModalSlidePanel
          isOpen={isRightPanelOpen}
          body={rightSideModule}
          onClose={() => setIsRightPanelOpen(false)}
          ariaLabel={t('common.side-panel')}
        />
      );
    }, [rightSideModule, isRightPanelOpen, isNarrowViewport, t]);

    const canOpenRightPanel = isNarrowViewport && Boolean(rightSideModule) && !isRightSideEmpty;

    if (!userProfileData || !userInfo || !channels.length || !config || !channel || !organisation) {
      return <LayoutLoader />;
    }

    const { title, faviconUrl } = config.elements.meta;

    return (
      <>
        <Helmet>
          <title>{title} - Admin</title>
          <link rel={'icon'} href={faviconUrl} />
        </Helmet>

        <ScrollToTop />

        <UserContext.Provider value={{ userProfile: userProfileData, setUserProfile, userInfo }}>
          <ConfigContext.Provider value={{ config }}>
            <OrganisationContext.Provider value={{ organisation }}>
              <ChannelContext.Provider value={{ channel, setChannel }}>
                <AudioPlayerContext.Provider value={{ audioPlayerData, setAudioPlayerData }}>
                  <DetailsModalContext.Provider value={{ detailsModalData, setDetailsModalData }}>
                    <EventDetailsModalContext.Provider
                      value={{ eventDetailsModalData, setEventDetailsModalData }}
                    >
                      <div className={classes['layout']}>
                        <SkipLink />

                        <Navbar />

                        <div className={classes['layout__page']}>
                          {!isNarrowViewport && !isLeaderBoard && <SidebarAdmin />}

                          <main
                            id={MAIN_CONTENT_ID}
                            className={classNames(classes['layout__content'], {
                              [classes['layout__content--full-width']]: fullWidthContent,
                              [classes['layout__content--full-width-no-sidebar']]: isLeaderBoard,
                              [classes['layout__content--narrow']]: isNarrowViewport,
                            })}
                          >
                            <Outlet />
                          </main>

                          <RightSideContext.Provider value={{ setIsRightSideEmpty }}>
                            {rightPanel}
                          </RightSideContext.Provider>
                        </div>

                        {canOpenRightPanel && (
                          <button
                            className={classes['layout__right-panel-filter']}
                            onClick={() => setIsRightPanelOpen(true)}
                            aria-label={t('common.open-side-panel')}
                          >
                            <IconLabel iconId={'side-panel'} iconSize={18} singleColor />
                          </button>
                        )}

                        <InAppMessageManager />
                      </div>

                      <TermsModal />

                      <AudioPlayer />

                      <DetailsModalAdmin />

                      <EventDetailsModal />
                    </EventDetailsModalContext.Provider>
                  </DetailsModalContext.Provider>
                </AudioPlayerContext.Provider>
              </ChannelContext.Provider>
            </OrganisationContext.Provider>
          </ConfigContext.Provider>
        </UserContext.Provider>

        {createPortal(
          <ToastContainer
            autoClose={2000}
            position={'bottom-center'}
            transition={Slide}
            newestOnTop
            hideProgressBar={true}
            pauseOnHover={false}
            draggable={false}
            style={{ zIndex: 999999 }}
          />,
          document.getElementById('toast-root') as HTMLElement
        )}

        {festiveModeComponent}
      </>
    );
  }
);
